import type { ComponentType } from 'react';

/**
 * 주소(쿼리)로 여닫는 오버레이 레지스트리. 딥링크가 필요한 상세 오버레이에 쓴다.
 *
 *   registerOverlay({ key: 'fundId', type: 'drawer', component: FundDetailDrawer });
 *   // → `?fundId=3` 이 주소에 있으면 FundDetailDrawer 가 뜬다. 뒤로 가기로 닫히고 링크를 공유할 수 있다.
 *
 * slice 가 자기 오버레이를 등록하고(모듈을 불러올 때), 프로바이더(`UrlOverlayHost`)가 그린다.
 */
export const URL_OVERLAY_TYPE = { MODAL: 'modal', DRAWER: 'drawer' } as const;
export type UrlOverlayType = (typeof URL_OVERLAY_TYPE)[keyof typeof URL_OVERLAY_TYPE];

export interface UrlOverlayProps {
  /** 쿼리 값(`?fundId=3` 의 `3`) */
  value: string;
  close: () => void;
}

export interface UrlOverlayDefinition {
  /** 쿼리 파라미터 이름. 앱 전체에서 하나여야 한다 */
  key: string;
  type: UrlOverlayType;
  component: ComponentType<UrlOverlayProps>;
}

const registry = new Map<string, UrlOverlayDefinition>();
const listeners = new Set<() => void>();
let snapshot: readonly UrlOverlayDefinition[] = [];

function publish() {
  snapshot = [...registry.values()];
  for (const listener of listeners) listener();
}

export function registerOverlay(definition: UrlOverlayDefinition): () => void {
  const existing = registry.get(definition.key);
  if (existing && existing.component !== definition.component) {
    throw new Error(`오버레이 key "${definition.key}" 가 이미 등록돼 있습니다.`);
  }
  registry.set(definition.key, definition);
  publish();
  return () => {
    if (registry.get(definition.key) === definition) {
      registry.delete(definition.key);
      publish();
    }
  };
}

export function subscribeUrlOverlays(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUrlOverlays(): readonly UrlOverlayDefinition[] {
  return snapshot;
}
